import { useEffect } from 'react';

/** `id` of the favicon link element declared in `index.html`. */
const FAVICON_ID = 'app-favicon';

export interface DocumentBrandingOptions {
  /** `Customer.FaviconUrl` from `whoami`, or null to keep the DecisionPoint default. */
  faviconUrl: string | null;
}

/**
 * Applies the effective customer's favicon by swapping the `href` on the
 * `<link rel="icon">` declared in `index.html` (WEB PRD §2.6), restoring the
 * DecisionPoint default when the customer changes to one without a favicon or
 * when the tree unmounts.
 *
 * <p>The URL is pre-loaded before the swap: a customer-hosted favicon on a dead
 * CDN link would otherwise leave the tab with no icon at all, which looks broken.
 * On load failure the default is kept — the same degrade-gracefully treatment
 * `PublicLayout` gives the customer logo. The load is also the reason this is an
 * effect rather than a render-time swap.</p>
 *
 * <p>Deliberately does NOT touch `document.title`: `ImpersonationBanner` owns the
 * title (it prefixes `[Impersonating]` and restores the captured original on
 * unmount), and a second writer would race it.</p>
 */
export function useDocumentBranding({ faviconUrl }: DocumentBrandingOptions) {
  useEffect(() => {
    const link = document.getElementById(FAVICON_ID) as HTMLLinkElement | null;
    if (!link) return;

    const defaultHref = link.dataset.defaultHref ?? link.getAttribute('href') ?? '';
    // Stash the default on first run so later restores don't read a branded value.
    if (!link.dataset.defaultHref) link.dataset.defaultHref = defaultHref;

    if (!faviconUrl) {
      link.setAttribute('href', defaultHref);
      return;
    }

    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (!cancelled) link.setAttribute('href', faviconUrl);
    };
    probe.onerror = () => {
      if (!cancelled) link.setAttribute('href', defaultHref);
    };
    probe.src = faviconUrl;

    return () => {
      cancelled = true;
      link.setAttribute('href', defaultHref);
    };
  }, [faviconUrl]);
}
